import Head from "next/head";
import Link from "next/link";
import dynamic from "next/dynamic";
import { getRandom } from "../../utils/blogUtils";
import blogData from "../../blogs.json";
import Service2 from "../../components/services/Service2";
const Capabilities2 = dynamic(() => import("../../components/services/Capabilities2"), { ssr: false });
import SuccessStoryEmbed from "../../components/trust/SuccessStoryEmbed";
import ServiceFAQ from "../../components/services/ServiceFAQ";
import ServiceEngagementLinks from "../../components/services/ServiceEngagementLinks";
import { CustomerSection } from "../../components/ai-agent-demo/CustomerSection";
const Footer = dynamic(() => import("../../components/Footer"), { ssr: false });

customSoftwareDevelopment.title = "Custom Software Development & Enterprise Engineering | ITMTB";
customSoftwareDevelopment.desc =
  "ITMTB builds custom software for enterprises — greenfield platforms, web and mobile apps, and AI-enabled systems — engineered for lifecycle ownership, not just delivery. Pune & Bhopal.";
customSoftwareDevelopment.header_img = "https://www.itmtb.com/services/capability/automate.webp";
customSoftwareDevelopment.keywords =
  "custom software development company India, custom software development Pune, custom software development Bhopal, enterprise software engineering India, bespoke software development India, AI-enabled software development India";

const faqItems = [
  {
    q: "What kinds of custom software does ITMTB build?",
    a: "Greenfield platforms, customer-facing and operational applications (web and mobile), bespoke enterprise systems, AI-enabled products, and the integrations that connect them — across BFSI, pharma, retail, supply chain, and manufacturing. The common thread is that off-the-shelf software either doesn't fit the process well enough or doesn't exist for it. We scope whether a custom build is genuinely warranted versus configuring an existing platform during the discovery sprint, and give you a direct recommendation either way.",
  },
  {
    q: "How do you price a custom software project?",
    a: "Through fixed-scope phases, capped time-and-materials, or outcome-linked retainers depending on how defined the scope is. Every engagement starts with a fixed-price discovery sprint, so you have a written estimate before committing to a build. Total cost is driven by scope, integration complexity, QA depth, and post-launch support rather than headcount — and we scope for what the system costs to own over five years, not just what it costs to ship in the first three months.",
    link: "/blog/custom-software-development-cost-india",
    linkLabel: "Read the cost guide",
  },
  {
    q: "Do you work with companies outside Pune and Bhopal?",
    a: "Yes. Our offices are in Pune and Bhopal, and we deliver custom software for clients across India and, on selected engagements, internationally. Whether you're nearby and want to meet in person or you're running a fully distributed engagement, the delivery model is the same: scoped, governed, and owned end to end.",
  },
  {
    q: "How long does a custom software project take?",
    a: "A fixed-scope first phase typically runs 4–8 weeks to production — the smallest shippable slice in real use before any larger commitment. Larger platforms are scoped from there based on complexity, integration surface, and the number of domains involved. The discovery sprint (typically two weeks, fixed price) produces a precise estimate and timeline for your specific system before any build begins.",
  },
  {
    q: "Do you maintain the software after launch?",
    a: "Yes. Every implementation ships with a 4–8 week post-deployment warranty period — defects and regressions fixed at no cost. Beyond that, optional managed-services engagements cover ongoing operation, monitoring, and evolution. We engineer for handover from day one: documentation, runbooks, and operational readiness so you're never dependent on us to run what we build.",
    link: "/services/managed-services",
    linkLabel: "See managed services",
  },
  {
    q: "How is this different from a development agency?",
    a: "We scope for lifecycle ownership rather than just shipping a build. Most software cost arrives after launch — support, defects, infrastructure, regression, maintenance — so we engineer to reduce that downstream drag: structured QA and release governance, observability built into delivery, documentation and operational readiness, and security architecture appropriate to regulated environments. The result is a system designed to run for years, not a prototype handed off for you to maintain alone.",
  },
  {
    q: "What does an engagement with ITMTB look like?",
    a: "Every engagement starts with a fixed-scope, fixed-price discovery sprint — typically 2 weeks. You leave with a written roadmap, risk register, cost estimate, and timeline before committing to a build. We then offer four engagement models: fixed-scope builds, time-and-materials with a budget cap, outcome-linked retainers, and managed services post-launch. If discovery doesn't justify proceeding, you can stop there — no obligation.",
    link: "/itmtb-how-we-work",
    linkLabel: "See how we work",
  },
];
customSoftwareDevelopment.faq = faqItems;

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Service",
      serviceType: "Custom Software Development",
      name: "Custom Software Development",
      provider: {
        "@type": "Organization",
        name: "Itmtb Technologies Private Limited",
        url: "https://www.itmtb.com",
        logo: "https://www.itmtb.com/logo.webp",
        sameAs: [
          "https://www.linkedin.com/company/itmtb-technologies/",
          "https://x.com/ITMTBTech",
          "https://www.instagram.com/itmtbtech/",
        ],
      },
      areaServed: [
        { "@type": "City", name: "Pune" },
        { "@type": "City", name: "Bhopal" },
        { "@type": "Country", name: "India" },
      ],
      url: "https://www.itmtb.com/services/custom-software-development",
      description:
        "Custom software development for enterprises — greenfield platforms, web and mobile applications, and AI-enabled systems, engineered for lifecycle ownership.",
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://www.itmtb.com" },
        { "@type": "ListItem", position: 2, name: "Services", item: "https://www.itmtb.com/capabilities" },
        {
          "@type": "ListItem",
          position: 3,
          name: "Custom Software Development",
          item: "https://www.itmtb.com/services/custom-software-development",
        },
      ],
    },
  ],
};

const activities = [
  "Greenfield platforms",
  "Web applications",
  "Mobile applications",
  "Bespoke enterprise systems",
  "AI-enabled products",
  "System integration",
  "API engineering",
  "Release governance",
  "Production operations",
];

const buildAreas = [
  {
    title: "Greenfield platforms",
    desc: "New products and internal systems built from the ground up, architected for scale from day one rather than retrofitted once they outgrow a prototype.",
  },
  {
    title: "Customer-facing and operational platforms",
    desc: "The web and mobile platforms your customers and operations run on — engineered for enterprise load and lifecycle ownership, not one-off delivery. Built with the QA, release governance, and observability that enterprise scale demands.",
  },
  {
    title: "Custom enterprise systems",
    desc: "Bespoke workflow, data, and operations software for processes where off-the-shelf doesn't fit and configuring a packaged product would produce something unmaintainable.",
  },
  {
    title: "AI-enabled products",
    desc: "Software with agentic and automation capabilities built in, not bolted on — agents that act on live business data through engineered, testable interfaces.",
  },
  {
    title: "System integration",
    desc: "Connecting custom software cleanly into the platforms you already run — ERPs, CRMs, payment rails, and data stores — so new systems extend the estate instead of fragmenting it.",
  },
];

const engineeringPrinciples = [
  {
    title: "Structured QA and release governance",
    desc: "Regression validation, release gating, and defined quality bars — the difference between software that ships once and software that keeps shipping safely.",
  },
  {
    title: "Observability and deployment maturity",
    desc: "Monitoring, logging, and deployment automation built into delivery so failures are visible and recoverable, not discovered by your users first.",
  },
  {
    title: "Documentation and operational readiness",
    desc: "Architecture docs, runbooks, and knowledge transfer so you can run what we build — independent of us — from the day it goes live.",
  },
  {
    title: "Security and access architecture",
    desc: "Access control, data handling, and compliance-aware design appropriate to regulated environments — BFSI, pharma, and supply chain — built in from the start.",
  },
];

const engagementModels = [
  {
    title: "Fixed-scope first phase",
    duration: "4–8 weeks · Fixed price",
    desc: "The smallest shippable slice in production before any larger commitment — so risk is proven down before scope expands.",
  },
  {
    title: "Time & materials with a budget cap",
    duration: "Flexible scope · Pre-agreed ceiling",
    desc: "Flexible scope within an agreed ceiling, with no invoice surprises — for work where requirements are still evolving.",
  },
  {
    title: "Post-deployment warranty",
    duration: "4–8 weeks post-handoff",
    desc: "Implementation defects and regressions fixed at no cost during the warranty period after handoff.",
  },
  {
    title: "No overhead billing",
    duration: "People on your project",
    desc: "You pay for people working on your project, not internal coordination or layers that don't touch the code.",
  },
];

const subServices = [
  {
    img: null,
    key: "Greenfield Platform Engineering",
    title: "New products and internal systems, architected for scale from the first decision — not retrofitted once they outgrow a prototype.",
    text: "We build greenfield platforms from the ground up: domain modelling, architecture, build, and production deployment. The early architecture decisions are where most long-term cost is set — so we make them deliberately, with scale, integration, and operational ownership in view from day one.\n\nThis is for products and internal systems where you're not adapting an existing tool, you're building the thing itself — and it has to run reliably for years, not demo once.",
  },
  {
    img: null,
    key: "Customer-Facing & Operational Platforms",
    title: "The platforms your customers and operations run on — engineered for enterprise load and lifecycle ownership, not one-off delivery.",
    text: "Web and mobile are delivery surfaces; the product is the system behind them — the data models, APIs, release governance, and observability that let a customer-facing or operational platform carry real business load and keep working as it evolves.\n\nThis is the difference between an application that demos well and one that holds up in production under real users, real data, and continuous change — with the QA and deployment maturity that enterprise use demands.",
  },
  {
    img: null,
    key: "Custom Enterprise Systems",
    title: "Bespoke workflow, data, and operations software for processes where off-the-shelf doesn't fit.",
    text: "Custom development is justified when a process is genuinely unique and a packaged product would require so much customisation it becomes unmaintainable — a proprietary logistics platform, a custom risk engine, a bespoke workflow connecting systems no product supports.\n\nWe scope whether custom is the right call versus configuring an existing platform during the discovery sprint, and give you an honest recommendation. Where custom is warranted, we build production-grade systems with proper testing, monitoring, and documentation.",
  },
  {
    img: null,
    key: "AI-Enabled Products",
    title: "Software with agentic and automation capabilities built in, not bolted on.",
    text: "We build software where AI is part of the architecture rather than a feature added at the end: agents that act on live business data, automation embedded in core workflows, and the engineered interfaces that let them do so safely.\n\nNon-deterministic systems need different engineering — behavioural testing, output validation, cost and latency guardrails, and clear boundaries on what an agent is allowed to do. We build for that from the start.",
  },
  {
    img: null,
    key: "System Integration",
    title: "Connecting custom software cleanly into the platforms you already run.",
    text: "Most custom software doesn't run in isolation — it has to reach into ERPs, CRMs, payment rails, data stores, and third-party services. We build versioned, testable integration layers so new systems extend your estate instead of fragmenting it.\n\nThis includes API engineering, event streaming for real-time data, and the access and security architecture that integration across systems requires.",
  },
];

export default function customSoftwareDevelopment() {
  let insights = blogData.filter(item => !["case-study", "tech"].includes(item?.meta?.category));
  let caseStudies = blogData.filter(item => item?.meta?.category === "case-study");

  insights = getRandom(insights, 2);
  caseStudies = getRandom(caseStudies, 2);

  return (
    <div>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
        />
      </Head>

      {/* Hero */}
      <Service2
        heading="Custom Software Development"
        paragraph={`<span style="display:block;font-weight:600;color:#1e293b;margin-bottom:16px">Enterprise-grade systems, built to own — not just ship.</span>We design and build custom software for organisations that need systems they can depend on for years — not a prototype, and not a hand-off you're left to maintain alone. From the first architecture decision through production deployment and sustained operation, we own the engineering lifecycle with you.<ul style="margin-top:16px;padding-left:20px;list-style:disc;display:flex;flex-direction:column;gap:8px"><li>Greenfield platforms, customer-facing and operational systems, and AI-enabled products</li><li>Engineered for lifecycle ownership — QA, observability, and handover built in</li><li>Fixed-price discovery sprint before any larger commitment</li><li>Delivered from Pune &amp; Bhopal, across India and internationally</li></ul>`}
        primaryCta={{ label: "Describe what you're building →", href: "/contact-us?utm_source=service-custom-software-development&utm_content=hero-cta" }}
        heroSvg={
          <svg viewBox="0 0 440 400" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-70" aria-hidden="true" role="presentation">
            {/* dot grid */}
            {[...Array(6)].map((_, row) =>
              [...Array(7)].map((_, col) => (
                <circle key={`${row}-${col}`} cx={40 + col * 60} cy={40 + row * 60} r="1.5" fill="#e2e8f0" />
              ))
            )}
            {/* lifecycle ring */}
            <circle cx="220" cy="200" r="150" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 4" />
            {/* lifecycle stage nodes */}
            <circle cx="220" cy="50" r="9" stroke="#94a3b8" strokeWidth="1.5" fill="white" />
            <circle cx="350" cy="125" r="9" stroke="#94a3b8" strokeWidth="1.5" fill="white" />
            <circle cx="350" cy="275" r="9" stroke="#94a3b8" strokeWidth="1.5" fill="white" />
            <circle cx="220" cy="350" r="9" stroke="#94a3b8" strokeWidth="1.5" fill="white" />
            <circle cx="90" cy="275" r="9" stroke="#94a3b8" strokeWidth="1.5" fill="white" />
            <circle cx="90" cy="125" r="9" stroke="#94a3b8" strokeWidth="1.5" fill="white" />
            {/* directional arc segment — amber accent (sustained ownership) */}
            <path d="M 90 125 A 150 150 0 0 1 220 50" stroke="#b45309" strokeWidth="2.5" fill="none" />
            {/* central core */}
            <circle cx="220" cy="200" r="34" stroke="#b45309" strokeWidth="1" strokeOpacity="0.3" strokeDasharray="5 3" />
            <circle cx="220" cy="200" r="22" stroke="#b45309" strokeWidth="2" />
            <circle cx="220" cy="200" r="8" fill="#b45309" fillOpacity="0.15" stroke="#b45309" strokeWidth="1.5" />
            {/* spokes to core */}
            <path d="M 220 72 L 220 178" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M 333 134 L 240 188" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M 333 266 L 240 212" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M 220 328 L 220 222" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M 107 266 L 200 212" stroke="#cbd5e1" strokeWidth="1.5" />
            <path d="M 107 134 L 200 188" stroke="#cbd5e1" strokeWidth="1.5" />
          </svg>
        }
      />

      {/* Anchor nav */}
      <nav className="bg-white border-b border-gray-100 px-6 md:px-[60px] py-2 overflow-x-auto">
        <div className="flex gap-6 text-sm font-medium whitespace-nowrap">
          {[
            { label: "What we build", href: "#what-we-build" },
            { label: "How we engineer", href: "#how-we-engineer" },
            { label: "How we engage", href: "#how-we-engage" },
            { label: "Where it goes deeper", href: "#goes-deeper" },
            { label: "FAQ", href: "#faq" },
          ].map(({ label, href }) => (
            <a key={href} href={href} className="text-gray-500 hover:text-indigo-600 transition-colors py-1">
              {label}
            </a>
          ))}
        </div>
      </nav>

      <CustomerSection title="Trusted by" />

      {/* § Definitional opener */}
      <section className="px-8 md:px-[60px] py-12 bg-white border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
            What is custom software development?
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            Custom software development is the design and engineering of software built specifically for an organisation's processes — rather than configuring an off-the-shelf product — covering everything from new greenfield platforms to customer-facing and operational applications, bespoke enterprise systems, and AI-enabled products.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
            The work is far more than a single build. The decisions that matter most — architecture, integration, quality, and operational readiness — determine what the system costs to own long after launch. Most software cost arrives <em>after</em> the first release: support, defects, infrastructure, regression, and maintenance — the{" "}
            <Link href="/blog/custom-software-development-cost-india">
              <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">total cost of ownership</a>
            </Link>{" "}
            that buyers routinely underestimate. Custom software development done well scopes for that whole lifecycle, not just the first three months of delivery.
          </p>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed">
            ITMTB builds and operates custom software for enterprises across BFSI, pharma, retail, supply chain, and manufacturing — delivered from offices in Pune and Bhopal, for clients across India and, on selected engagements, internationally.
          </p>
        </div>
      </section>

      {/* § What we build */}
      <section id="what-we-build" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            What We Build
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-10">
            Custom software development covers far more than a single build. We work across the full range of enterprise engineering — with one common thread: we scope for what the system costs to own over five years, not just what it costs to build in the first three months.
          </p>

          <div className="space-y-10">
            {buildAreas.map(area => (
              <div key={area.title}>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{area.title}</h3>
                <p className="text-gray-600 text-base leading-relaxed">{area.desc}</p>
              </div>
            ))}
          </div>

          {/* activity grid */}
          <p className="text-xs font-semibold uppercase tracking-widest text-gray-400 mt-12 mb-5">
            Typical custom software activities
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {activities.map(activity => (
              <div
                key={activity}
                className="flex items-center gap-3 bg-white border border-gray-100 rounded-lg px-4 py-3"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 flex-shrink-0" />
                <span className="text-sm text-gray-700">{activity}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* § How we engineer */}
      <section id="how-we-engineer" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            How We Engineer
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-10">
            Most software cost arrives <em>after</em> launch — support, defects, infrastructure, regression, maintenance. We engineer to reduce that downstream drag, so the system you ship is the system you can keep running.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {engineeringPrinciples.map(principle => (
              <div key={principle.title} className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="text-base font-bold text-gray-900 mb-2">{principle.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{principle.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success stories */}
      <SuccessStoryEmbed
        maxItems={3}
        heading="Selected custom software work"
      />

      <section className="px-8 md:px-[60px] py-8 bg-white border-t border-gray-100">
        <p className="max-w-4xl text-sm text-gray-500 leading-relaxed">
          For a regulated custom-build example, read how we designed a{" "}
          <Link href="/blog/pharma-compliance-ai-sop-bmr-warning-letter-review">
            <a className="text-indigo-600 font-medium hover:text-indigo-800 transition-colors">
              pharma compliance AI platform for SOP and BMR review
            </a>
          </Link>{" "}
          with versioned documents, traceable findings, and reviewer control.
        </p>
      </section>

      {/* § How we engage */}
      <section id="how-we-engage" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">How We Engage</h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            We structure engagements so contract risk matches the work — and so you prove risk down before scope expands. Every engagement starts with a fixed-scope, fixed-price discovery sprint.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            {engagementModels.map(model => (
              <div key={model.title} className="bg-white border border-gray-200 rounded-xl p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-indigo-500 mb-2">
                  {model.duration}
                </p>
                <h3 className="text-base font-bold text-gray-900 mb-2">{model.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{model.desc}</p>
              </div>
            ))}
          </div>
          <Link href="/itmtb-how-we-work">
            <a className="inline-flex items-center text-sm font-semibold text-indigo-600 hover:text-indigo-800 transition-colors">
              See all engagement options →
            </a>
          </Link>
        </div>
      </section>

      {/* Capabilities */}
      <Capabilities2
        dataList={subServices}
        bg="bg-[#402A6A]"
        insights={[...caseStudies, ...insights]}
        hideFooter
        hideCardSubtitle
      />

      {/* § Where it goes deeper */}
      <section id="goes-deeper" className="scroll-mt-24 px-8 md:px-[60px] py-14 bg-white border-t border-gray-100">
        <div className="max-w-5xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
            Where It Goes Deeper
          </h2>
          <p className="text-gray-600 text-base md:text-lg mb-10">
            Custom software development is the umbrella. Several specialised practices sit beneath it — engage the one that matches where you are.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              {
                href: "/services/enterprise-applications",
                label: "Enterprise Applications",
                desc: "Modernising or extending an existing ERP or CRM — SAP, Microsoft Dynamics 365, Oracle — and making legacy systems agent-ready.",
              },
              {
                href: "/services/cloud",
                label: "Cloud",
                desc: "The cloud layer your software runs on — architecture, migration, and operations on AWS, Azure, and GCP.",
              },
              {
                href: "/services/quality-engineering",
                label: "Quality Engineering",
                desc: "Test automation and release quality — including testing strategies for non-deterministic AI systems.",
              },
              {
                href: "/services/iot-and-digital-eng",
                label: "IoT & Digital Engineering",
                desc: "Connected devices, edge intelligence, and digital engineering for connected products.",
              },
              {
                href: "/services/managed-services",
                label: "Managed Services",
                desc: "Run and evolve the software after launch — operations, monitoring, and ongoing support with defined SLAs.",
              },
            ].map(link => (
              <Link key={link.href} href={link.href}>
                <a className="block bg-gray-50 border border-gray-200 rounded-xl p-6 hover:border-indigo-400 hover:shadow-sm transition-all group">
                  <h3 className="text-base font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors mb-2">
                    {link.label}
                  </h3>
                  <p className="text-sm text-gray-500 leading-relaxed">{link.desc}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-indigo-500 group-hover:text-indigo-700 transition-colors">
                    Learn more →
                  </span>
                </a>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* § Built in India */}
      <section className="px-8 md:px-[60px] py-14 bg-gray-50 border-t border-gray-100">
        <div className="max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-5">
            Built in India, working with you wherever you are
          </h2>
          <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
            We work from offices in <strong>Pune</strong> and <strong>Bhopal</strong>, delivering custom software for clients across India and, on selected engagements, internationally. Whether you're nearby and want to meet in person or you're running a distributed engagement, the delivery model is the same: scoped, governed, and owned end to end.
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600">
            <span className="font-semibold text-gray-900">Pune</span>
            <span className="text-gray-300">·</span>
            <span className="font-semibold text-gray-900">Bhopal</span>
            <span className="text-gray-300">·</span>
            <a href="mailto:hello@itmtb.com" className="text-indigo-600 hover:text-indigo-800 transition-colors">
              hello@itmtb.com
            </a>
          </div>
        </div>
      </section>

      <div id="faq" className="scroll-mt-24"><ServiceFAQ items={faqItems} /></div>

      <ServiceEngagementLinks />

      {/* Page-specific CTA */}
      <section className="px-8 md:px-[60px] py-16 bg-[#0d0822]">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-xs font-semibold uppercase tracking-widest text-indigo-400 mb-5">
            Building something custom?
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-5">
            Tell us what you need
          </h2>
          <p className="text-slate-300 text-base md:text-lg leading-relaxed mb-8">
            We'll scope a fixed-price first phase before any larger commitment — the
            smallest shippable slice in production, so you prove the risk down before
            scope expands.
          </p>
          <Link href="/contact-us?utm_source=service-custom-software-development&utm_content=bottom-cta">
            <a className="inline-block bg-[#f1aa4e] text-black font-semibold text-base px-8 py-3.5 rounded-md hover:bg-[#e09a3e] transition-colors">
              Describe what you're building →
            </a>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
